import HeartButton from "@/components/HeartButton";
import ProductImage from "@/components/ProductImage";
import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Clock, Images, MapPin, Phone, Search, ShieldCheck, Star, Store, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/Pagination";
import { PET_TYPES } from "@/lib/sites";
import { toast } from "sonner";
import OfferBadge, { Price } from "@/components/OfferBadge";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const mediaUrl = (p) => (p && p.startsWith("/") ? `${BACKEND}${p}` : p);

export function VendorRatingBadge({ summary, compact = false, testid = "vendor-rating-badge" }) {
  if (!summary || !summary.count) return null;
  const items = [
    ["Product", summary.product_quality_avg],
    ["Vendor", summary.vendor_experience_avg],
    ["Delivery", summary.delivery_experience_avg],
  ];
  return (
    <div
      className={`rounded-2xl border border-marigold/40 bg-marigold/5 ${compact ? "px-3 py-2" : "px-4 py-3"} min-w-[180px]`}
      data-testid={testid}
    >
      <div className={`flex items-center gap-1 ${compact ? "text-[10px]" : "text-xs"} uppercase tracking-widest text-marigold font-bold`}>
        <Star className={`${compact ? "h-2.5 w-2.5" : "h-3 w-3"} fill-marigold`} /> Ratings
        <span className="text-stone-500 font-normal normal-case tracking-normal ml-auto">
          {summary.count} review{summary.count === 1 ? "" : "s"}
        </span>
      </div>
      <div className={`mt-1.5 grid grid-cols-3 gap-2 ${compact ? "text-[10px]" : "text-xs"}`}>
        {items.map(([label, v]) => (
          <div key={label} className="flex flex-col items-center text-stone-700">
            <div className={`font-bold text-stoneWarm ${compact ? "text-sm" : "text-base"}`}>
              {v != null ? v.toFixed(1) : "—"}
            </div>
            <div className="text-stone-500">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function VendorPublic() {
  const { id } = useParams();
  const [vendor, setVendor] = useState(null);

  useEffect(() => {
    if (!id) return;
    api.get(`/vendors/${id}`).then(r => setVendor(r.data)).catch(() => {});
  }, [id]);

  useSeo(vendor ? {
    title: `${vendor.shop_name}: pet supplies delivered${vendor.city ? ` in ${vendor.city}` : ""}`,
    description: `Order pet food, treats and supplies from ${vendor.shop_name}${vendor.city ? `, ${vendor.city}` : ""} on PawBazaar. Hyperlocal same-day pet supply delivery.`,
    path: `/vendor/${id}`,
    jsonLd: { "@context": "https://schema.org", "@type": "PetStore", name: vendor.shop_name,
      address: { "@type": "PostalAddress", streetAddress: vendor.shop_address, addressLocality: vendor.city, postalCode: vendor.pincode, addressCountry: "IN" },
      ...(vendor.rating_summary?.count > 0 ? { aggregateRating: { "@type": "AggregateRating", ratingValue: vendor.rating_summary.avg, reviewCount: vendor.rating_summary.count } } : {}) },
  } : {});
  if (!vendor) return <div className="max-w-5xl mx-auto px-6 py-10">Loading vendor...</div>;
  const rs = vendor.rating_summary;
  const photos = (vendor.shop_photos || []).filter(Boolean);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      {/* Storefront photos: one big, the next two beside it, all of them in a viewer */}
      {photos.length > 0 && <ShopGallery photos={photos} name={vendor.shop_name} />}

      <Card className="p-5 sm:p-8 rounded-3xl border-stone-200 mb-8" data-testid="vendor-public-header">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          {photos.length === 0 && (
            <div className="h-20 w-20 rounded-3xl bg-marigold/15 border border-marigold/30 flex items-center justify-center overflow-hidden">
              <Store className="h-8 w-8 text-marigold" strokeWidth={2.5} />
            </div>
          )}
          <div className="flex-1">
            {vendor.shop_category && <Badge className="bg-terracotta/10 text-terracotta border-0 rounded-full mb-2">{vendor.shop_category}</Badge>}
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-heading text-3xl font-semibold tracking-tight">{vendor.shop_name}</h1>
              <span className="inline-flex items-center gap-1 rounded-full bg-teal/10 text-teal px-2.5 py-0.5 text-xs font-bold"><ShieldCheck className="h-3.5 w-3.5" /> Verified</span>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${vendor.open_now ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-600"}`} data-testid="vendor-open-now">
                {vendor.open_now ? `Open now${vendor.closes_at ? ` · till ${niceTime(vendor.closes_at)}` : ""}` : "Closed now · orders go out when it opens"}
              </span>
            </div>
            {vendor.shop_description && (
              <p className="mt-2 text-sm text-stone-700 leading-relaxed" data-testid="vendor-public-description">{vendor.shop_description}</p>
            )}
            <div className="mt-3 space-y-1 text-sm text-stone-600">
              {vendor.shop_address && (
                <div className="flex items-center gap-2"><MapPin className="h-4 w-4" />{vendor.shop_address}{vendor.city ? `, ${vendor.city}` : ""} · {vendor.pincode}</div>
              )}
              {vendor.contact_number && <div className="flex items-center gap-2"><Phone className="h-4 w-4" />{vendor.contact_number}</div>}
              {(vendor.hours_text || vendor.operating_hours_note || (typeof vendor.operating_hours === "string" && vendor.operating_hours)) && <div className="flex items-center gap-2" data-testid="vendor-public-hours"><Clock className="h-4 w-4" />{vendor.hours_text || vendor.operating_hours_note || vendor.operating_hours}</div>}
              {vendor.owner_name && <div className="flex items-center gap-2"><Store className="h-4 w-4" />Owner: {vendor.owner_name}</div>}
            </div>
          </div>
          {rs && rs.count > 0 && (
            <VendorRatingBadge summary={rs} testid="vendor-public-rating" />
          )}
        </div>
      </Card>

      <ShopOffers offers={vendor.offers} />

      <ShopProducts vendorId={id} />
    </div>
  );
}

// "21:00" → "9 pm"
const niceTime = (hm) => {
  const [h, m] = String(hm).split(":").map(Number);
  if (Number.isNaN(h)) return hm;
  return `${((h + 11) % 12) + 1}${m ? `:${String(m).padStart(2, "0")}` : ""} ${h < 12 ? "am" : "pm"}`;
};

const SORTS = [["recommended", "Recommended"], ["price_low", "Price: low to high"], ["price_high", "Price: high to low"], ["name", "Name A–Z"]];
const PER_PAGE = 24;

/** The shop's products: search inside this shop, filter, sort, a page at a time. */
function ShopProducts({ vendorId }) {
  const [f, setF] = useState({ q: "", category: "", pet_type: "", on_offer: false, in_stock: false, sort: "recommended" });
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); setPage(1); };
  const load = useCallback(() => {
    const params = { ...f, page, per_page: PER_PAGE };
    Object.keys(params).forEach((k) => (params[k] === "" || params[k] === false) && delete params[k]);
    return api.get(`/vendors/${vendorId}/products`, { params }).then((r) => setD(r.data), () => setD({ items: [], total: 0, page: 1, pages: 1, categories: [] }));
  }, [vendorId, f, page]);
  useEffect(() => { const t = setTimeout(load, f.q ? 300 : 0); return () => clearTimeout(t); }, [load, f.q]);
  const filtered = f.q || f.category || f.pet_type || f.on_offer || f.in_stock;
  const select = "h-10 rounded-full border border-stone-200 bg-white px-3 text-sm";
  return (
    <div data-testid="vendor-public-products">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-stone-500 mr-auto">Products {d ? `(${d.total})` : ""}</div>
        <div className="relative w-full sm:w-72">
          <Search className="h-4 w-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Search in this shop" className="pl-9 rounded-full" data-testid="shop-search" />
        </div>
        <select value={f.sort} onChange={(e) => set("sort", e.target.value)} className={select} data-testid="shop-sort" aria-label="Sort">
          {SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <select value={f.pet_type} onChange={(e) => set("pet_type", e.target.value)} className={select} data-testid="shop-pet" aria-label="Pet">
          <option value="">All pets</option>{PET_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}</select>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4" data-testid="shop-categories">
        {[{ name: "", count: null }, ...(d?.categories || [])].map((c) => (
          <button key={c.name || "all"} type="button" onClick={() => set("category", c.name)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-sm border ${f.category === c.name ? "bg-stoneWarm text-white border-stoneWarm" : "bg-white border-stone-200 text-stone-700"}`}>
            {c.name || "All"}{c.count != null ? <span className="opacity-60"> {c.count}</span> : null}</button>
        ))}
        <button type="button" onClick={() => set("on_offer", !f.on_offer)} data-testid="shop-on-offer"
          className={`shrink-0 rounded-full px-3 py-1.5 text-sm border ${f.on_offer ? "bg-terracotta text-white border-terracotta" : "bg-white border-stone-200 text-stone-700"}`}>On offer</button>
        <button type="button" onClick={() => set("in_stock", !f.in_stock)} data-testid="shop-in-stock"
          className={`shrink-0 rounded-full px-3 py-1.5 text-sm border ${f.in_stock ? "bg-teal text-white border-teal" : "bg-white border-stone-200 text-stone-700"}`}>In stock</button>
      </div>
      {d === null ? <div className="text-sm text-stone-500">Loading products…</div> : d.items.length === 0 ? (
        <Card className="p-6 rounded-2xl border-stone-200 border-dashed text-sm text-stone-600" data-testid="vendor-public-no-products">
          {filtered ? "Nothing in this shop matches. Try another search or clear the filters." : "This shop hasn't listed any products yet."}
        </Card>
      ) : (<>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {d.items.map(p => (
          <Link key={p.id} to={`/product/${p.id}`}>
            <Card className="overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all border-stone-200 rounded-2xl cursor-pointer h-full flex flex-col">
              <div className="relative aspect-square bg-stone-100 overflow-hidden">
                <ProductImage src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                <HeartButton p={p} className="absolute top-2 right-2" />
                <OfferBadge deal={p.deal} />
                {p.num_in_stock === 0 && <span className="absolute bottom-2 left-2 rounded-full bg-stone-900/70 text-white text-[10px] px-2 py-0.5">Out of stock</span>}
              </div>
              <div className="p-3 flex flex-col flex-1">
                <div className="text-sm font-semibold line-clamp-2 leading-tight flex-1">{p.name}</div>
                <div className="text-[10px] uppercase tracking-wide text-stone-500 truncate mt-1">
                  {p.brand ? `${p.brand} · ` : ""}{p.size_count > 1 ? `${p.size_count} sizes` : (p.size_variant || "")}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span><Price price={p.min_price ?? p.price} mrp={p.size_count > 1 ? null : p.best_offer?.mrp} /></span>
                  {p.subscription_eligible && <Badge className="bg-teal/15 text-teal hover:bg-teal/15 border-0 text-[10px] rounded-full">Sub</Badge>}
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>
      <Pagination page={d.page} pageCount={d.pages} onChange={(n) => { setPage(n); window.scrollTo({ top: 0, behavior: "smooth" }); }} total={d.total} pageSize={PER_PAGE} testidPrefix="shop-pg" />
      </>)}
    </div>
  );
}

/** One large photo, two beside it (with "+N"), and a full-screen viewer with arrows, swipe and keys. */
function ShopGallery({ photos, name }) {
  const [open, setOpen] = useState(null);
  const n = photos.length;
  const go = useCallback((step) => setOpen((i) => (i === null ? i : (i + step + n) % n)), [n]);
  useEffect(() => {
    if (open === null) return undefined;
    const key = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); if (e.key === "Escape") setOpen(null); };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [open, go]);
  const [touch, setTouch] = useState(null);
  const shown = photos.slice(0, 3);
  return (
    <>
      <div className={`grid gap-3 mb-6 ${n === 1 ? "grid-cols-1" : "grid-cols-3 grid-rows-2 h-[260px] sm:h-[380px]"}`} data-testid="vendor-public-photos">
        {shown.map((p, i) => (
          <button key={p} type="button" onClick={() => setOpen(i)} data-testid={`vendor-public-photo-${i}`}
            className={`relative rounded-3xl overflow-hidden bg-stone-100 border border-stone-200 ${n === 1 ? "aspect-[21/9]" : i === 0 ? "col-span-2 row-span-2" : n === 2 ? "row-span-2" : ""}`}>
            <img src={mediaUrl(p)} alt={`${name} photo ${i + 1}`} className="w-full h-full object-cover" />
            {i === shown.length - 1 && n > shown.length && (
              <span className="absolute inset-0 bg-black/45 text-white flex items-center justify-center gap-1 font-semibold"><Images className="h-5 w-5" />+{n - shown.length} photos</span>
            )}
          </button>
        ))}
      </div>
      {open !== null && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" role="dialog" aria-label={`${name} photos`} data-testid="gallery-viewer"
          onClick={() => setOpen(null)}
          onTouchStart={(e) => setTouch(e.touches[0].clientX)}
          onTouchEnd={(e) => { if (touch === null) return; const dx = e.changedTouches[0].clientX - touch; if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); setTouch(null); }}>
          <img src={mediaUrl(photos[open])} alt={`${name} photo ${open + 1}`} className="max-h-[85vh] max-w-[92vw] object-contain rounded-xl" onClick={(e) => e.stopPropagation()} />
          <button type="button" aria-label="Close" className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/15 text-white flex items-center justify-center" onClick={() => setOpen(null)}><X className="h-5 w-5" /></button>
          {n > 1 && <>
            <button type="button" aria-label="Previous" className="absolute left-3 h-11 w-11 rounded-full bg-white/15 text-white flex items-center justify-center" onClick={(e) => { e.stopPropagation(); go(-1); }} data-testid="gallery-prev"><ChevronLeft className="h-6 w-6" /></button>
            <button type="button" aria-label="Next" className="absolute right-3 h-11 w-11 rounded-full bg-white/15 text-white flex items-center justify-center" onClick={(e) => { e.stopPropagation(); go(1); }} data-testid="gallery-next"><ChevronRight className="h-6 w-6" /></button>
            <div className="absolute bottom-5 flex gap-1.5" onClick={(e) => e.stopPropagation()}>
              {photos.map((p, i) => <button key={p} type="button" aria-label={`Photo ${i + 1}`} onClick={() => setOpen(i)} className={`h-2 w-2 rounded-full ${i === open ? "bg-white" : "bg-white/40"}`} />)}
            </div>
            <div className="absolute top-5 left-5 text-white/80 text-sm" data-testid="gallery-count">{open + 1} / {n}</div>
          </>}
        </div>
      )}
    </>
  );
}


/** The shop's running sale offers and coupon codes (shop_offers). */
function ShopOffers({ offers }) {
  if (!offers || (!offers.deals?.length && !offers.coupons?.length)) return null;
  const copy = (code) => {
    navigator.clipboard?.writeText(code).then(() => toast.success(`${code} copied. Use it at checkout.`), () => {});
  };
  const until = (d) => (d ? ` · until ${new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" })}` : "");
  return (
    <div className="mb-8 flex flex-wrap gap-3" data-testid="vendor-public-offers">
      {offers.deals.map((d) => (
        <div key={d.id} className="rounded-2xl bg-terracotta/10 border border-terracotta/20 px-4 py-2 text-sm">
          <b className="text-terracotta">{d.label}</b> <span className="text-stone-700">{d.title}</span>
          <span className="text-stone-500 text-xs">{d.scope === "all" ? " · everything" : ""}{until(d.ends_on)}</span>
        </div>
      ))}
      {offers.coupons.map((c) => (
        <button key={c.code} type="button" onClick={() => copy(c.code)} className="rounded-2xl bg-marigold/10 border border-marigold/30 px-4 py-2 text-sm text-left" data-testid="vendor-public-coupon">
          Use <b className="font-mono">{c.code}</b>: {c.label}<span className="text-stone-500 text-xs">{until(c.ends_on)}</span>
        </button>
      ))}
    </div>
  );
}
